import React, { useState, useEffect, useCallback } from 'react';

// CONFIGURACIÓN - Tu token de Flap
const TOKEN_ADDRESS = '0x7fb7224379045941277ae688cad9c823c9d27777';
const DEAD_ADDRESS = '0x000000000000000000000000000000000000dead';
const DONATION_API_URL = `https://t3w1p53k7a.execute-api.eu-west-3.amazonaws.com/donation?token=${TOKEN_ADDRESS}`;
const BINANCE_PRICE_API = 'https://api.binance.com/api/v3/ticker/price?symbol=BNBUSDT';

function App() {
  const [burnedBNB, setBurnedBNB] = useState(0);
  const [burnHistory, setBurnHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bnbPrice, setBnbPrice] = useState(600); // Precio por defecto
  const [stats, setStats] = useState({
    totalBurned: 0,
    lastUpdate: null,
    estimatedUSD: 0
  });

  // Función para obtener el precio de BNB en tiempo real
  const getBNBPrice = useCallback(async () => {
    try {
      const response = await fetch(BINANCE_PRICE_API);
      if (!response.ok) {
        console.warn('Could not fetch BNB price, using default');
        return bnbPrice; // Usar precio actual si falla
      }
      const data = await response.json();
      const price = parseFloat(data.price);
      console.log(`BNB Price: $${price.toFixed(2)}`);
      return price;
    } catch (error) {
      console.warn('Error fetching BNB price:', error);
      return bnbPrice; // Usar precio actual si falla
    }
  }, [bnbPrice]);

  // Función para obtener datos de la API oficial del protocolo
  const getBurnDataFromAPI = async () => {
    try {
      console.log('Fetching burn data from official API...');
      console.log('API URL:', DONATION_API_URL);
      
      const response = await fetch(DONATION_API_URL);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('API Response:', data);
      
      // La API devuelve: { token: "0x...", totalDonated: "11.03067324298402", cached: true }
      let totalBNB = 0;
      
      if (data.totalDonated !== undefined) {
        totalBNB = parseFloat(data.totalDonated);
        console.log(`Total burned: ${totalBNB.toFixed(8)} BNB`);
      } else {
        console.error('No se encontró totalDonated en la respuesta:', data);
      }
      
      return totalBNB;
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(`Error: ${error.message}. Abre la consola (F12) para más detalles.`);
      return 0;
    }
  };

  // Actualizar el balance cada 10 segundos
  useEffect(() => {
    const updateBalance = async () => {
      try {
        // Obtener datos en paralelo
        const [balance, currentBnbPrice] = await Promise.all([
          getBurnDataFromAPI(),
          getBNBPrice()
        ]);
        
        const now = new Date();
        
        setBurnedBNB(balance);
        setBnbPrice(currentBnbPrice);
        
        // Agregar a historial (mantener últimas 50 lecturas)
        setBurnHistory(prev => {
          const newHistory = [...prev, {
            time: now.toLocaleTimeString(),
            bnb: balance,
            timestamp: now.getTime()
          }];
          return newHistory.slice(-50);
        });

        setStats({
          totalBurned: balance,
          lastUpdate: now.toLocaleTimeString(),
          estimatedUSD: balance * currentBnbPrice
        });

        setIsLoading(false);
        setError(null);
      } catch (err) {
        console.error('Update error:', err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    // Primera carga
    updateBalance();

    // Actualizar cada 10 segundos
    const interval = setInterval(updateBalance, 10000);

    return () => clearInterval(interval);
  }, [burnHistory, getBNBPrice]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '3rem',
        color: 'white'
      }}>
        <h1 style={{
          fontSize: '3rem',
          fontWeight: 'bold',
          marginBottom: '0.5rem',
          textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
        }}>
          销毁BNB BURN TRACKER
        </h1>
        <p style={{
          fontSize: '1.2rem',
          opacity: 0.9
        }}>
          Live tracking for 销毁BNB
        </p>
        <p style={{
          fontSize: '0.9rem',
          opacity: 0.7,
          marginTop: '0.5rem',
          fontFamily: 'monospace'
        }}>
          {TOKEN_ADDRESS}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #ef4444',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '2rem',
          color: '#991b1b'
        }}>
          <strong>⚠️ Connection Error:</strong>
          <div style={{ marginTop: '0.5rem' }}>{error}</div>
          <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#7f1d1d' }}>
            <strong>Tip:</strong> Open browser console (F12) and look for "API Response" to see what the API returns.
          </div>
        </div>
      )}

      {/* Connection Status */}
      {!error && isLoading && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '2rem',
          color: '#92400e'
        }}>
          <strong>⏳ Connecting to official API...</strong>
          <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
            Fetching burn data from protocol...
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1.5rem',
        marginBottom: '3rem'
      }}>
        {/* Total Burned */}
        <div style={{
          background: 'white',
          borderRadius: '1rem',
          padding: '2rem',
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
          transition: 'transform 0.3s ease',
          cursor: 'default'
        }}>
          <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
            🔥 Total BNB Burned
          </div>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#667eea' }}>
            {isLoading ? '...' : burnedBNB.toFixed(8)}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.5rem' }}>
            From 销毁BNB token
          </div>
        </div>

        {/* USD Value */}
        <div style={{
          background: 'white',
          borderRadius: '1rem',
          padding: '2rem',
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
        }}>
          <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
            💰 Estimated USD Value
          </div>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#10b981' }}>
            ${(stats.estimatedUSD || 0).toLocaleString('en-US', {maximumFractionDigits: 2})}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.5rem' }}>
            @ ${(bnbPrice || 600).toFixed(2)}/BNB (Live)
          </div>
        </div>

        {/* Last Update */}
        <div style={{
          background: 'white',
          borderRadius: '1rem',
          padding: '2rem',
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
        }}>
          <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
            🕐 Last Update
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#8b5cf6' }}>
            {stats.lastUpdate || '...'}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#10b981', marginTop: '0.5rem' }}>
            ● Live - Auto-refresh: 10s
          </div>
        </div>
      </div>

      {/* Info Boxes */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Token Info */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '1rem',
          padding: '1.5rem',
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
        }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
            🪙 Token Info
          </h3>
          <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
            <strong>Contract Address:</strong>
          </div>
          <code style={{
            display: 'block',
            background: '#f3f4f6',
            padding: '0.5rem',
            borderRadius: '0.5rem',
            fontSize: '0.75rem',
            wordBreak: 'break-all',
            fontFamily: 'monospace',
            marginBottom: '1rem'
          }}>
            {TOKEN_ADDRESS}
          </code>
          <a 
            href={`https://bscscan.com/token/${TOKEN_ADDRESS}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              background: '#667eea',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            View on BSCScan →
          </a>
        </div>

        {/* Dead Address Info */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '1rem',
          padding: '1.5rem',
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
        }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>
            💀 Burn Address
          </h3>
          <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
            <strong>Tracking Burns To:</strong>
          </div>
          <code style={{
            display: 'block',
            background: '#f3f4f6',
            padding: '0.5rem',
            borderRadius: '0.5rem',
            fontSize: '0.75rem',
            wordBreak: 'break-all',
            fontFamily: 'monospace',
            marginBottom: '1rem'
          }}>
            {DEAD_ADDRESS}
          </code>
          <a 
            href={`https://bscscan.com/address/${DEAD_ADDRESS}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              background: '#10b981',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            View on BSCScan →
          </a>
        </div>
      </div>

      {/* Footer Info */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '1rem',
        padding: '1.5rem',
        textAlign: 'center',
        boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
      }}>
        <p style={{ color: '#666', marginBottom: '0.5rem', fontSize: '0.95rem' }}>
          💡 <strong>How it works:</strong> This dashboard uses the official protocol API to track BNB burned from 销毁BNB
        </p>
        <p style={{ color: '#999', fontSize: '0.85rem', marginTop: '1rem' }}>
          Data refreshes automatically every 10 seconds • Powered by Tax Token Protocol API
        </p>
        <div style={{ marginTop: '1.5rem' }}>
          <a 
            href="https://x.com/i/communities/2011755431119429962"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              background: '#1DA1F2',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              textDecoration: 'none',
              fontSize: '0.95rem',
              fontWeight: '600',
              transition: 'background 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.background = '#1a8cd8'}
            onMouseOut={(e) => e.target.style.background = '#1DA1F2'}
          >
            🐦 Join X Community
          </a>
        </div>
      </div>
    </div>
  );
}

export default App;